/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/repair-knowledge-base
 * Password-gated. noindex.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AdminLoginGate, isAdminAuthed, clearAdminAuth } from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import {
  useRepairCases,
  fetchRepairCase,
  createRepairCase,
  saveRepairCase,
  deleteRepairCase,
  uploadRepairMedia,
  updateRepairMedia,
  deleteRepairMedia,
  fetchSuggestions,
  searchRepairReferences,
  fetchLinkedReferences,
  linkCaseToReference,
  unlinkCaseFromReference,
  emptyError,
  emptyMeasurement,
  emptyVoltageInjection,
  emptyComponent,
  DEVICE_FAMILIES,
  REPAIR_CATEGORIES,
  RESULT_OPTIONS,
  MEASUREMENT_TYPES,
  MEASUREMENT_UNITS,
  ERROR_SOURCES,
  COMPONENT_TYPES,
  FAILURE_MODES,
} from '../../lib/repair-kb-client';
import type {
  RepairCase,
  RepairCaseDraft,
  RepairError,
  RepairMeasurement,
  RepairVoltageInjection,
  RepairComponent,
  RepairMedia,
  RepairStatus,
  RepairReference,
  LinkedReference,
} from '../../lib/repair-kb-client';
import {
  ArrowLeft, Plus, Trash2, Edit3, Loader, Upload, Save,
  ChevronDown, ChevronUp, X, Check, AlertTriangle, Image,
  FileText, Zap, Cpu, Hash, Search,
} from '../../lib/icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusBadge(status: RepairStatus) {
  const cls = {
    draft:    'bg-yellow-900/40 text-yellow-400 border-yellow-800',
    published:'bg-green-900/40 text-green-400 border-green-800',
    internal: 'bg-blue-900/40 text-blue-400 border-blue-800',
  }[status];
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${cls}`}>
      {status.toUpperCase()}
    </span>
  );
}

function btn(variant: 'primary' | 'secondary' | 'danger' | 'ghost', extra = '') {
  const base = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50';
  const variants = {
    primary:   'bg-[#FF1744] hover:bg-[#D50000] text-white',
    secondary: 'bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] border border-gray-700',
    danger:    'bg-red-950/40 hover:bg-red-900/50 text-red-400 border border-red-900/60',
    ghost:     'text-gray-400 hover:text-[#E0E0E0] hover:bg-gray-800',
  };
  return `${base} ${variants[variant]} ${extra}`;
}

function Input({ label, value, onChange, placeholder = '', type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-400 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
      />
    </div>
  );
}

function Textarea({ label, value, onChange, rows = 3, placeholder = '', className = '' }: {
  label: string; value: string; onChange: (v: string) => void;
  rows?: number; placeholder?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-400 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 resize-y"
      />
    </div>
  );
}

function Select<T extends string>({ label, value, onChange, options, className = '' }: {
  label: string; value: T; onChange: (v: T) => void;
  options: { value: T; label: string }[]; className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-semibold text-gray-400 mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value as T)}
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-[#FF1744]/60"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SectionHeader({ icon, title, count }: { icon: React.ReactNode; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[#FF1744]">{icon}</div>
      <h3 className="text-base font-bold text-[#E0E0E0]">{title}</h3>
      {count !== undefined && (
        <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">{count}</span>
      )}
      <div className="h-px flex-1 bg-gray-800" />
    </div>
  );
}

// ── Autocomplete input for board revision ─────────────────────────────────────
function AutocompleteInput({ label, value, onChange, suggestions }: {
  label: string; value: string; onChange: (v: string) => void; suggestions: string[];
}) {
  const [open, setOpen] = useState(false);
  const filtered = suggestions.filter(s => s.toLowerCase().includes(value.toLowerCase()) && s !== value);
  return (
    <div className="relative">
      <label className="block text-xs font-semibold text-gray-400 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="e.g. EDM-030"
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-auto">
          {filtered.slice(0, 8).map(s => (
            <li key={s}>
              <button
                type="button"
                onMouseDown={() => onChange(s)}
                className="w-full text-left px-3 py-2 text-sm text-[#E0E0E0] hover:bg-gray-800"
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Tag / multi-select chip input ─────────────────────────────────────────────
function ChipInput({ label, value, onChange, options }: {
  label: string; value: string[]; onChange: (v: string[]) => void; options: string[];
}) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-400 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const active = value.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                active
                  ? 'bg-[#FF1744]/20 text-[#FF1744] border-[#FF1744]/60'
                  : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Tag text input (comma-separated) ─────────────────────────────────────────
function TagInput({ label, value, onChange }: {
  label: string; value: string[]; onChange: (v: string[]) => void;
}) {
  const [raw, setRaw] = useState(value.join(', '));
  const commit = () => {
    onChange(raw.split(',').map(t => t.trim()).filter(Boolean));
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-400 mb-1">{label}</label>
      <input
        type="text"
        value={raw}
        onChange={e => setRaw(e.target.value)}
        onBlur={commit}
        placeholder="PS5, EDM-030, No Power, F3502 (comma separated)"
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60"
      />
    </div>
  );
}

// ── Symptom tag input ─────────────────────────────────────────────────────────
const SYMPTOM_SUGGESTIONS = [
  'No Power', 'One Beep', 'Blue Light of Death', 'BLOD',
  'No HDMI Output', 'Powers Off After 5 Seconds', 'Powers Off After 30 Seconds',
  'High Current Draw', 'Low Current Draw', 'Short to Ground',
  'No Image', 'Disc Not Detected', 'WiFi Not Working',
];

function SymptomInput({ label, value, onChange }: {
  label: string; value: string[]; onChange: (v: string[]) => void;
}) {
  const [text, setText] = useState('');
  const add = (s: string) => {
    const cleaned = s.trim();
    if (cleaned && !value.includes(cleaned)) onChange([...value, cleaned]);
    setText('');
  };
  const remove = (s: string) => onChange(value.filter(v => v !== s));
  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); if (text.trim()) add(text); }
  };
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-400 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map(s => (
          <span key={s} className="flex items-center gap-1 px-2 py-0.5 bg-gray-800 border border-gray-700 rounded-full text-xs text-[#E0E0E0]">
            {s}
            <button type="button" onClick={() => remove(s)} className="text-gray-500 hover:text-red-400"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKey}
        onBlur={() => { if (text.trim()) add(text); }}
        placeholder="Type symptom, press Enter"
        className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#FF1744]/60 mb-2"
      />
      <div className="flex flex-wrap gap-1.5">
        {SYMPTOM_SUGGESTIONS.filter(s => !value.includes(s)).map(s => (
          <button key={s} type="button" onClick={() => add(s)}
            className="px-2 py-0.5 text-[10px] text-gray-500 border border-gray-700 rounded-full hover:border-gray-500 hover:text-gray-300 transition-all">
            + {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Repeatable row helpers ────────────────────────────────────────────────────
function move<T>(arr: T[], from: number, to: number): T[] {
  const a = [...arr];
  const [item] = a.splice(from, 1);
  a.splice(to, 0, item);
  return a.map((r, i) => ({ ...r, sort_order: i }));
}

// ── Error codes section ───────────────────────────────────────────────────────
function ErrorCodesSection({ rows, onChange }: { rows: RepairError[]; onChange: (r: RepairError[]) => void }) {
  const update = (i: number, patch: Partial<RepairError>) =>
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i).map((r, j) => ({ ...r, sort_order: j })));
  const add    = () => onChange([...rows, { ...emptyError(), sort_order: rows.length }]);

  return (
    <div>
      <SectionHeader icon={<Hash className="h-4 w-4" />} title="Diagnostic / Error Codes" count={rows.length} />
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
            <Select label="Source" value={row.source} onChange={v => update(i, { source: v })} options={ERROR_SOURCES} />
            <Input label="Error Code" value={row.error_code} onChange={v => update(i, { error_code: v })} placeholder="80801602" />
            <Input label="Notes" value={row.notes} onChange={v => update(i, { notes: v })} placeholder="Optional explanation" className="md:col-span-1" />
            <div className="flex items-end justify-end">
              <button type="button" onClick={() => remove(i)} className={btn('danger', 'mt-5')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className={`mt-3 ${btn('secondary')}`}>
        <Plus className="h-4 w-4" /> Add Error Code
      </button>
    </div>
  );
}

// ── Measurements section ──────────────────────────────────────────────────────
function MeasurementsSection({ rows, onChange }: { rows: RepairMeasurement[]; onChange: (r: RepairMeasurement[]) => void }) {
  const update = (i: number, patch: Partial<RepairMeasurement>) =>
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i).map((r, j) => ({ ...r, sort_order: j })));
  const add    = () => onChange([...rows, { ...emptyMeasurement(), sort_order: rows.length }]);

  return (
    <div>
      <SectionHeader icon={<Zap className="h-4 w-4" />} title="Board Measurements" count={rows.length} />
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-start">
              <Input label="Reference / Line" value={row.reference_line} onChange={v => update(i, { reference_line: v })} placeholder="F3502, PP3V3" />
              <Select label="Type" value={row.measurement_type} onChange={v => update(i, { measurement_type: v })} options={MEASUREMENT_TYPES} />
              <Input label="Expected Value" value={row.expected_value} onChange={v => update(i, { expected_value: v })} placeholder="12" />
              <Input label="Measured Value" value={row.measured_value} onChange={v => update(i, { measured_value: v })} placeholder="0" />
              <Select label="Units" value={row.units} onChange={v => update(i, { units: v })} options={MEASUREMENT_UNITS} />
              <div className="flex items-end justify-end gap-2">
                <button type="button" onClick={() => remove(i)} className={btn('danger', 'mt-5')}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={row.is_verified_reference}
                  onChange={e => update(i, { is_verified_reference: e.target.checked })}
                  className="rounded border-gray-700 bg-black/40 text-[#FF1744] focus:ring-[#FF1744]/50"
                />
                <span className="text-xs text-gray-400">Verified reference value (not just this repair)</span>
              </label>
              <Input label="" value={row.notes} onChange={v => update(i, { notes: v })} placeholder="Notes" className="flex-1" />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className={`mt-3 ${btn('secondary')}`}>
        <Plus className="h-4 w-4" /> Add Measurement
      </button>
    </div>
  );
}

// ── Voltage injection section ─────────────────────────────────────────────────
function VoltageInjectionSection({ rows, onChange }: { rows: RepairVoltageInjection[]; onChange: (r: RepairVoltageInjection[]) => void }) {
  const update = (i: number, patch: Partial<RepairVoltageInjection>) =>
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i).map((r, j) => ({ ...r, sort_order: j })));
  const add    = () => onChange([...rows, { ...emptyVoltageInjection(), sort_order: rows.length }]);

  return (
    <div>
      <SectionHeader icon={<Zap className="h-4 w-4" />} title="Voltage Injection" count={rows.length} />
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            <Input label="Injection Point" value={row.injection_point} onChange={v => update(i, { injection_point: v })} placeholder="F3502" />
            <Input label="Injected Voltage" value={row.injected_voltage} onChange={v => update(i, { injected_voltage: v })} placeholder="e.g. 3.3V" />
            <Input label="Current Limit" value={row.current_limit} onChange={v => update(i, { current_limit: v })} placeholder="e.g. 100mA" />
            <Input label="Result" value={row.result} onChange={v => update(i, { result: v })} placeholder="e.g. Shorted capacitor located" className="md:col-span-2" />
            <Input label="Component That Heated" value={row.component_heated} onChange={v => update(i, { component_heated: v })} placeholder="Reference designator if known" />
            <Textarea label="Notes" value={row.notes} onChange={v => update(i, { notes: v })} rows={2} className="md:col-span-2" />
            <div className="flex items-end justify-end">
              <button type="button" onClick={() => remove(i)} className={btn('danger')}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className={`mt-3 ${btn('secondary')}`}>
        <Plus className="h-4 w-4" /> Add Voltage Injection
      </button>
    </div>
  );
}

// ── Failed components section ─────────────────────────────────────────────────
function ComponentsSection({ rows, onChange }: { rows: RepairComponent[]; onChange: (r: RepairComponent[]) => void }) {
  const update = (i: number, patch: Partial<RepairComponent>) =>
    onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i).map((r, j) => ({ ...r, sort_order: j })));
  const add    = () => onChange([...rows, { ...emptyComponent(), sort_order: rows.length }]);

  return (
    <div>
      <SectionHeader icon={<Cpu className="h-4 w-4" />} title="Fault / Failed Components" count={rows.length} />
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
            <Input label="Component Reference" value={row.component_ref} onChange={v => update(i, { component_ref: v })} placeholder="C1234, F3502, IC5600" />
            <Select label="Component Type" value={row.component_type} onChange={v => update(i, { component_type: v })} options={COMPONENT_TYPES} />
            <Select label="Failure Mode" value={row.failure_mode} onChange={v => update(i, { failure_mode: v })} options={FAILURE_MODES} />
            <div className="flex flex-col gap-2">
              <Input label="Notes" value={row.notes} onChange={v => update(i, { notes: v })} placeholder="Optional notes" />
              <div className="flex justify-end">
                <button type="button" onClick={() => remove(i)} className={btn('danger')}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className={`mt-3 ${btn('secondary')}`}>
        <Plus className="h-4 w-4" /> Add Component
      </button>
    </div>
  );
}

// ── Media section ─────────────────────────────────────────────────────────────
function MediaSection({ caseId, media, onChange }: {
  caseId: string | undefined;
  media: RepairMedia[];
  onChange: (m: RepairMedia[]) => void;
}) {
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]  = useState(0);
  const [editCaption, setEditCaption] = useState<{ id: string; caption: string } | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !caseId) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      try {
        const m = await uploadRepairMedia(caseId, file, p => setProgress(p));
        onChange([...media, m]);
      } catch (err) {
        alert(`Upload failed: ${(err as Error).message}`);
      }
    }
    setUploading(false);
    setProgress(0);
  };

  const handleDelete = async (m: RepairMedia) => {
    if (!caseId || !m.id) return;
    if (!window.confirm('Delete this media? This cannot be undone.')) return;
    await deleteRepairMedia(caseId, m.id);
    onChange(media.filter(x => x.id !== m.id));
  };

  const handleCaptionSave = async () => {
    if (!editCaption || !caseId) return;
    await updateRepairMedia(caseId, editCaption.id, { caption: editCaption.caption });
    onChange(media.map(m => m.id === editCaption.id ? { ...m, caption: editCaption.caption } : m));
    setEditCaption(null);
  };

  const handleFeatured = async (m: RepairMedia) => {
    if (!caseId || !m.id) return;
    // Unset all, then set this one
    const updated = media.map(x => ({ ...x, is_featured: x.id === m.id }));
    for (const x of updated) {
      if (x.id) await updateRepairMedia(caseId, x.id, { is_featured: x.is_featured });
    }
    onChange(updated);
  };

  const handleReorder = async (from: number, to: number) => {
    if (!caseId) return;
    const reordered = move(media, from, to);
    onChange(reordered);
    for (const m of reordered) {
      if (m.id) await updateRepairMedia(caseId, m.id, { sort_order: m.sort_order });
    }
  };

  return (
    <div>
      <SectionHeader icon={<Image className="h-4 w-4" />} title="Photos & Videos" count={media.length} />

      {!caseId && (
        <p className="text-sm text-yellow-500 bg-yellow-900/20 border border-yellow-800 rounded-lg p-3 mb-4">
          Save the entry first before uploading media.
        </p>
      )}

      {/* Upload zone */}
      {caseId && (
        <label className="block border-2 border-dashed border-gray-700 rounded-xl p-6 text-center cursor-pointer hover:border-gray-500 transition-all mb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
            className="sr-only"
            onChange={e => handleFiles(e.target.files)}
            disabled={uploading}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader className="h-6 w-6 text-[#FF1744] animate-spin" />
              <p className="text-sm text-gray-400">Uploading… {progress}%</p>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Drop photos/videos or click to upload</p>
              <p className="text-xs text-gray-600 mt-1">JPG, PNG, WebP, MP4, MOV, WebM</p>
            </>
          )}
        </label>
      )}

      {/* Media grid */}
      {media.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...media].sort((a, b) => a.sort_order - b.sort_order).map((m, i) => {
            const isVideo = m.mime_type.startsWith('video/');
            return (
              <div key={m.id ?? i} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden flex flex-col">
                <div className="aspect-video bg-gray-950 relative overflow-hidden">
                  {isVideo ? (
                    <video src={m.public_url} muted playsInline className="w-full h-full object-cover" />
                  ) : (
                    <img src={m.public_url} alt={m.caption || 'Media'} className="w-full h-full object-cover" />
                  )}
                  {m.is_featured && (
                    <span className="absolute top-1 left-1 bg-[#FF1744] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                      FEATURED
                    </span>
                  )}
                </div>
                <div className="p-2 flex flex-col gap-1 flex-1">
                  {editCaption?.id === m.id ? (
                    <div className="flex gap-1">
                      <input
                        autoFocus
                        type="text"
                        value={editCaption.caption}
                        onChange={e => setEditCaption({ ...editCaption, caption: e.target.value })}
                        className="flex-1 bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-[#E0E0E0]"
                        onKeyDown={e => { if (e.key === 'Enter') handleCaptionSave(); if (e.key === 'Escape') setEditCaption(null); }}
                      />
                      <button type="button" onClick={handleCaptionSave} className="text-green-400 hover:text-green-300">
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => m.id && setEditCaption({ id: m.id, caption: m.caption })}
                      className="text-left text-xs text-gray-500 hover:text-gray-300 truncate">
                      {m.caption || '+ Add caption'}
                    </button>
                  )}
                  <div className="flex items-center gap-1 mt-auto">
                    <button type="button" onClick={() => handleFeatured(m)} title="Set as featured"
                      className={`flex-1 text-[10px] py-1 rounded transition-all ${m.is_featured ? 'text-[#FF1744]' : 'text-gray-600 hover:text-gray-400'}`}>
                      ★
                    </button>
                    {i > 0 && (
                      <button type="button" onClick={() => handleReorder(i, i - 1)} className="text-gray-600 hover:text-gray-400">
                        <ChevronUp className="h-3 w-3" />
                      </button>
                    )}
                    {i < media.length - 1 && (
                      <button type="button" onClick={() => handleReorder(i, i + 1)} className="text-gray-600 hover:text-gray-400">
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    )}
                    <button type="button" onClick={() => handleDelete(m)} className="text-red-800 hover:text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Linked board references (admin picker) ────────────────────────────────────
function LinkedReferencesAdminSection({
  caseId,
  onExpired,
}: {
  caseId: string | undefined;
  onExpired: () => void;
}) {
  const [linked,    setLinked]    = useState<LinkedReference[]>([]);
  const [query,     setQuery]     = useState('');
  const [results,   setResults]   = useState<RepairReference[]>([]);
  const [searching, setSearching] = useState(false);
  const [err,       setErr]       = useState<string | null>(null);

  useEffect(() => {
    if (!caseId) return;
    fetchLinkedReferences(caseId).then(setLinked).catch(() => {});
  }, [caseId]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const refs = await searchRepairReferences(query);
        setResults(refs.filter(r => !linked.some(l => l.id === r.id)));
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, linked]);

  const handleLink = async (ref: RepairReference) => {
    if (!caseId) return;
    setErr(null);
    try {
      await linkCaseToReference(caseId, ref.id);
      setLinked(prev => [...prev, {
        id: ref.id, slug: ref.slug ?? null, title: ref.title,
        category: ref.category ?? null, device_family: ref.device_family ?? null,
        device_model: ref.device_model ?? null, board_revision: ref.board_revision ?? null,
        verification_status: ref.verification_status ?? null,
      }]);
      setResults(prev => prev.filter(r => r.id !== ref.id));
      setQuery('');
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    }
  };

  const handleUnlink = async (ref: LinkedReference) => {
    if (!caseId) return;
    setErr(null);
    try {
      await unlinkCaseFromReference(caseId, ref.id);
      setLinked(prev => prev.filter(l => l.id !== ref.id));
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      setErr(msg);
    }
  };

  if (!caseId) {
    return (
      <p className="text-sm text-yellow-500 bg-yellow-900/20 border border-yellow-800 rounded-lg p-3">
        Save the entry first before linking board references.
      </p>
    );
  }

  return (
    <div>
      <SectionHeader icon={<FileText className="h-4 w-4" />} title="Linked Board References" count={linked.length} />

      {err && <p className="text-red-400 text-xs mb-3">{err}</p>}

      {/* Currently linked */}
      {linked.length > 0 && (
        <div className="space-y-2 mb-4">
          {linked.map(ref => (
            <div key={ref.id} className="flex items-center gap-3 bg-gray-900 border border-[#00BFFF]/20 rounded-xl px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#E0E0E0] truncate">{ref.title}</p>
                <p className="text-xs text-gray-500 truncate">
                  {[ref.device_family, ref.board_revision, ref.measurement_type ?? ref.category].filter(Boolean).join(' · ')}
                </p>
              </div>
              <a
                href={`/repair-database/ref/${ref.slug ?? ref.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#00BFFF] hover:underline shrink-0"
              >
                View ↗
              </a>
              <button
                type="button"
                onClick={() => handleUnlink(ref)}
                className={btn('danger', '!px-2 !py-1')}
                title="Unlink"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search to link */}
      <div className="relative">
        <div className="flex items-center gap-2 bg-black/40 border border-gray-700 rounded-lg px-3 py-2 focus-within:border-[#00BFFF]/60 transition-colors">
          <Search className="h-4 w-4 text-gray-500 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search board references to link…"
            className="flex-1 bg-transparent text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none"
          />
          {searching && <Loader className="h-3.5 w-3.5 text-gray-500 animate-spin shrink-0" />}
        </div>

        {results.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-h-60 overflow-auto">
            {results.map(ref => (
              <li key={ref.id}>
                <button
                  type="button"
                  onMouseDown={() => handleLink(ref)}
                  className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-gray-800 transition-colors border-b border-gray-800/60 last:border-0"
                >
                  <Plus className="h-3.5 w-3.5 text-[#00BFFF] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-[#E0E0E0] truncate">{ref.title}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {[ref.device_family, ref.board_revision, ref.measurement_type ?? ref.category].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Case list ─────────────────────────────────────────────────────────────────
function CaseList({ onExpired }: { onExpired: () => void }) {
  const navigate = useNavigate();
  const { cases, loading, error, refetch } = useRepairCases(true);

  useEffect(() => {
    if (error && error.toLowerCase().includes('unauthorized')) onExpired();
  }, [error, onExpired]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('');

  const filtered = cases.filter(c => {
    const q = search.toLowerCase();
    const matchesQ = !q || [c.title, c.device_family, c.board_revision, ...c.tags, ...c.symptoms_summary]
      .some(s => s.toLowerCase().includes(q));
    const matchesStatus = !filter || c.status === filter;
    return matchesQ && matchesStatus;
  });

  const handleDelete = async (c: RepairCase) => {
    if (!window.confirm(`Delete "${c.title || c.slug}"? This cannot be undone.`)) return;
    try {
      await deleteRepairCase(c.id);
      refetch();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      alert(`Delete failed: ${msg}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead title="Repair Knowledge Base | GameFix Admin" description="Internal repair KB" noindex />

      <div className="sticky top-0 z-40 bg-[#0D0D0D]/95 backdrop-blur border-b border-gray-800">
        {/* Breadcrumb */}
        <div className="px-6 pt-2.5 flex items-center gap-1.5 text-xs text-gray-600">
          <Link to="/admin/edit-site" className="hover:text-gray-400 transition-colors">Admin</Link>
          <span>›</span>
          <span className="text-gray-400">Repair Knowledge Base</span>
        </div>
        {/* Title row */}
        <div className="px-6 py-2 flex items-center gap-3 flex-wrap">
          <Link to="/admin/edit-site" className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E0E0E0] transition-colors flex-shrink-0">
            <ArrowLeft className="h-4 w-4" /> Back to Admin
          </Link>
          <h1 className="text-base font-bold text-[#E0E0E0] flex-1 min-w-0">Repair Knowledge Base</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={refetch} className="text-xs text-gray-500 hover:text-[#E0E0E0]">Refresh</button>
            <button onClick={() => navigate('/admin/repair-knowledge-base/new')} className={btn('primary')}>
              <Plus className="h-4 w-4" /> New Repair Entry
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="px-6 flex gap-1">
          <button className="px-4 py-2.5 text-sm font-medium border-b-2 border-[#FF1744] text-[#FF1744]">
            Repair Cases
          </button>
          <Link to="/admin/repair-knowledge-base/refs"
            className="px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-[#E0E0E0] transition-colors">
            Known-Good References
          </Link>
          <Link to="/admin/repair-knowledge-base/taxonomy"
            className="px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-[#E0E0E0] transition-colors ml-auto">
            Manage Taxonomy
          </Link>
          <Link to="/admin/repair-knowledge-base/ads"
            className="px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-[#E0E0E0] transition-colors">
            Ad Settings
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search cases…"
              className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-9 pr-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-600"
            />
          </div>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="internal">Internal</option>
          </select>
        </div>

        {loading && <div className="flex items-center justify-center py-20 gap-3 text-gray-500"><Loader className="h-5 w-5 animate-spin" /> Loading…</div>}
        {error && <div className="bg-red-950/40 border border-red-800 rounded-xl p-4 text-red-400 text-sm">{error}</div>}

        {!loading && filtered.length === 0 && (
          <div className="text-center py-20 text-gray-600">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="mb-4">{cases.length === 0 ? 'No repair entries yet.' : 'No results.'}</p>
            {cases.length === 0 && (
              <button onClick={() => navigate('/admin/repair-knowledge-base/new')} className={btn('primary')}>
                <Plus className="h-4 w-4" /> Create First Entry
              </button>
            )}
          </div>
        )}

        <div className="space-y-3">
          {filtered.map(c => {
            const deviceLabel = DEVICE_FAMILIES.find(d => d.value === c.device_family)?.label ?? c.device_family;
            return (
              <div key={c.id} className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl p-4 flex items-center gap-4 transition-all">
                {c.featured_image_url ? (
                  <img src={c.featured_image_url} alt="" className="h-14 w-20 object-cover rounded-lg flex-shrink-0 bg-gray-800" />
                ) : (
                  <div className="h-14 w-20 bg-gray-800 rounded-lg flex-shrink-0 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-gray-700" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    {statusBadge(c.status)}
                    <span className="text-[10px] text-gray-600">{deviceLabel}</span>
                    {c.board_revision && <span className="text-[10px] font-mono text-gray-500">{c.board_revision}</span>}
                    {c.result && <span className="text-[10px] text-gray-500">{RESULT_OPTIONS.find(r => r.value === c.result)?.label}</span>}
                  </div>
                  <p className="text-sm font-semibold text-[#E0E0E0] truncate">{c.title || c.slug}</p>
                  {c.symptoms_summary.length > 0 && (
                    <p className="text-xs text-gray-500 truncate">{c.symptoms_summary.join(' · ')}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => navigate(`/admin/repair-knowledge-base/${c.id}`)} className={btn('secondary')}>
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(c)} className={btn('danger')}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Case editor ───────────────────────────────────────────────────────────────
type FormState = Omit<RepairCase, 'id' | 'created_at' | 'updated_at' | 'published_at' |
  'repair_case_errors' | 'repair_case_measurements' | 'repair_case_voltage_injections' |
  'repair_case_components' | 'repair_case_media'> & {
  errors:             RepairError[];
  measurements:       RepairMeasurement[];
  voltage_injections: RepairVoltageInjection[];
  components:         RepairComponent[];
  media:              RepairMedia[];
};

function blankForm(): FormState {
  return {
    slug: '', status: 'draft', title: '', seo_title: '', seo_description: '',
    device_family: '', device_model: '', board_revision: '',
    repair_categories: [], symptoms_summary: [], detailed_symptoms: '',
    root_cause: '', repair_summary: '', components_replaced: '',
    jumper_trace_repair: '', post_repair_testing: '', result: '',
    public_notes: '', internal_notes: '', featured_image_url: '',
    tags: [],
    errors: [], measurements: [], voltage_injections: [], components: [], media: [],
  };
}

function CaseEditor({ caseId, onExpired }: { caseId: string | undefined; onExpired: () => void }) {
  const navigate = useNavigate();
  const isNew  = !caseId || caseId === 'new';
  const savedId = useRef<string | undefined>(isNew ? undefined : caseId);

  const [form, setForm]         = useState<FormState>(blankForm());
  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);
  const [boardSugs, setBoardSugs] = useState<string[]>([]);

  // Load existing case
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    fetchRepairCase(caseId!, true)
      .then(c => {
        setForm({
          slug: c.slug, status: c.status, title: c.title,
          seo_title: c.seo_title, seo_description: c.seo_description,
          device_family: c.device_family, device_model: c.device_model,
          board_revision: c.board_revision,
          repair_categories: c.repair_categories, symptoms_summary: c.symptoms_summary,
          detailed_symptoms: c.detailed_symptoms, root_cause: c.root_cause,
          repair_summary: c.repair_summary, components_replaced: c.components_replaced,
          jumper_trace_repair: c.jumper_trace_repair, post_repair_testing: c.post_repair_testing,
          result: c.result, public_notes: c.public_notes, internal_notes: c.internal_notes ?? '',
          featured_image_url: c.featured_image_url, tags: c.tags,
          errors:             c.repair_case_errors             ?? [],
          measurements:       c.repair_case_measurements       ?? [],
          voltage_injections: c.repair_case_voltage_injections ?? [],
          components:         c.repair_case_components         ?? [],
          media:              c.repair_case_media              ?? [],
        });
        setLoading(false);
      })
      .catch(e => { setToast({ msg: e.message, ok: false }); setLoading(false); });
  }, [caseId, isNew]);

  // Load board revision suggestions
  useEffect(() => {
    fetchSuggestions('board_revision').then(setBoardSugs).catch(() => {});
  }, []);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSave = async (andNew = false) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload: RepairCaseDraft = {
        slug: form.slug || undefined,
        status: form.status, title: form.title,
        seo_title: form.seo_title, seo_description: form.seo_description,
        device_family: form.device_family, device_model: form.device_model,
        board_revision: form.board_revision,
        repair_categories: form.repair_categories, symptoms_summary: form.symptoms_summary,
        detailed_symptoms: form.detailed_symptoms, root_cause: form.root_cause,
        repair_summary: form.repair_summary, components_replaced: form.components_replaced,
        jumper_trace_repair: form.jumper_trace_repair, post_repair_testing: form.post_repair_testing,
        result: form.result, public_notes: form.public_notes, internal_notes: form.internal_notes,
        featured_image_url: form.featured_image_url, tags: form.tags,
        errors:             form.errors,
        measurements:       form.measurements,
        voltage_injections: form.voltage_injections,
        components:         form.components,
      };

      if (isNew && !savedId.current) {
        const created = await createRepairCase(payload);
        savedId.current = created.id;
        showToast('Entry created!', true);
        if (andNew) {
          navigate('/admin/repair-knowledge-base/new');
          setForm(blankForm());
          savedId.current = undefined;
        } else {
          navigate(`/admin/repair-knowledge-base/${created.id}`, { replace: true });
        }
      } else {
        const id = savedId.current ?? caseId!;
        await saveRepairCase(id, payload);
        showToast('Saved!', true);
        if (andNew) navigate('/admin/repair-knowledge-base/new');
      }
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.toLowerCase().includes('unauthorized')) { onExpired(); return; }
      showToast(msg, false);
    } finally {
      setSaving(false);
    }
  };

  const deviceLabel = DEVICE_FAMILIES.find(d => d.value === form.device_family)?.label ?? '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center gap-3 text-gray-500">
        <Loader className="h-6 w-6 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead
        title={isNew ? 'New Repair Entry | GameFix Admin' : `Edit: ${form.title || form.slug} | GameFix Admin`}
        description="Internal repair KB editor"
        noindex
      />

      {/* Nav bar */}
      <div className="sticky top-0 z-30 bg-[#0D0D0D]/95 backdrop-blur border-b border-gray-800">
        {/* Breadcrumb */}
        <div className="px-6 pt-2 flex items-center gap-1.5 text-xs text-gray-600">
          <Link to="/admin/edit-site" className="hover:text-gray-400 transition-colors">Admin</Link>
          <span>›</span>
          <Link to="/admin/repair-knowledge-base" className="hover:text-gray-400 transition-colors">Repair Knowledge Base</Link>
          <span>›</span>
          <span className="text-gray-400">{isNew ? 'New Repair Entry' : 'Edit Repair Entry'}</span>
        </div>
        <div className="px-6 py-2 flex items-center gap-4 flex-wrap">
        <button onClick={() => navigate('/admin/repair-knowledge-base')}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#E0E0E0] transition-colors flex-shrink-0">
          <ArrowLeft className="h-4 w-4" /> Back to Repair Cases
        </button>
        <h1 className="text-base font-bold flex-1 min-w-0 truncate">
          {isNew ? 'New Repair Entry' : (form.title || form.slug || 'Edit Entry')}
        </h1>

        {/* Status selector */}
        <select value={form.status} onChange={e => set('status', e.target.value as RepairStatus)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="internal">Internal Only</option>
        </select>

        {/* Save buttons */}
        <button onClick={() => handleSave(false)} disabled={saving} className={btn('primary')}>
          {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => handleSave(true)} disabled={saving} className={btn('secondary')}>
          Save & Add Another
        </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-sm font-semibold border ${
          toast.ok ? 'bg-green-900/90 border-green-700 text-green-300' : 'bg-red-900/90 border-red-700 text-red-300'
        }`}>
          {toast.ok ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">

        {/* ── Basic Information ─────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-bold text-[#E0E0E0] border-b border-gray-800 pb-3">Basic Information</h2>

          <Input label="Title (auto-generated if left blank)" value={form.title} onChange={v => set('title', v)}
            placeholder="PS5 EDM-030 — No Power / F3502 Short" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select label="Device Family" value={form.device_family} onChange={v => set('device_family', v)}
              options={[{ value: '', label: 'Select device…' }, ...DEVICE_FAMILIES]} />
            <Input label="Model" value={form.device_model} onChange={v => set('device_model', v)}
              placeholder="PS5 Slim, Xbox Series X" />
            <AutocompleteInput label="Board Revision (optional)" value={form.board_revision}
              onChange={v => set('board_revision', v)} suggestions={boardSugs} />
          </div>

          <ChipInput label="Repair Category" value={form.repair_categories}
            onChange={v => set('repair_categories', v)} options={REPAIR_CATEGORIES} />
        </section>

        {/* ── Symptoms ─────────────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-bold text-[#E0E0E0] border-b border-gray-800 pb-3">Symptoms</h2>
          <SymptomInput label="Symptoms (quick tags)" value={form.symptoms_summary}
            onChange={v => set('symptoms_summary', v)} />
          <Textarea label="Detailed Symptoms" value={form.detailed_symptoms}
            onChange={v => set('detailed_symptoms', v)} rows={4}
            placeholder="Describe what the customer reported and what you observed…" />
        </section>

        {/* ── Diagnostic Codes ─────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <ErrorCodesSection rows={form.errors} onChange={v => set('errors', v)} />
        </section>

        {/* ── Board Measurements ───────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <MeasurementsSection rows={form.measurements} onChange={v => set('measurements', v)} />
        </section>

        {/* ── Voltage Injection ────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <VoltageInjectionSection rows={form.voltage_injections} onChange={v => set('voltage_injections', v)} />
        </section>

        {/* ── Failed Components ────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <ComponentsSection rows={form.components} onChange={v => set('components', v)} />
        </section>

        {/* ── Repair Performed ─────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-bold text-[#E0E0E0] border-b border-gray-800 pb-3">Repair Performed</h2>
          <Textarea label="Root Cause" value={form.root_cause} onChange={v => set('root_cause', v)} rows={3}
            placeholder="What caused the failure?" />
          <Textarea label="Repair Performed" value={form.repair_summary} onChange={v => set('repair_summary', v)} rows={4}
            placeholder="Describe what you did to repair the board…" />
          <Textarea label="Components Replaced" value={form.components_replaced} onChange={v => set('components_replaced', v)} rows={2}
            placeholder="List any components that were replaced…" />
          <Textarea label="Jumper / Trace Repair" value={form.jumper_trace_repair} onChange={v => set('jumper_trace_repair', v)} rows={2}
            placeholder="Describe any jumper wires or trace repairs…" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Final Result" value={form.result} onChange={v => set('result', v)} options={RESULT_OPTIONS} />
            <Input label="Featured Image URL (optional)" value={form.featured_image_url}
              onChange={v => set('featured_image_url', v)} placeholder="Set via ★ button in media section" />
          </div>

          <Textarea label="Post-Repair Testing" value={form.post_repair_testing} onChange={v => set('post_repair_testing', v)} rows={3}
            placeholder="What was tested after the repair? Results?" />
        </section>

        {/* ── Notes ────────────────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6 space-y-5">
          <h2 className="text-base font-bold text-[#E0E0E0] border-b border-gray-800 pb-3">Notes</h2>
          <Textarea label="Public Repair Notes" value={form.public_notes} onChange={v => set('public_notes', v)} rows={4}
            placeholder="Notes visible on the public website…" />
          <div>
            <Textarea label="Internal Technician Notes" value={form.internal_notes} onChange={v => set('internal_notes', v)} rows={4}
              placeholder="Admin-only notes. NEVER shown publicly." />
            <p className="text-[10px] text-yellow-600 mt-1 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Internal notes are never included in public API responses or HTML.
            </p>
          </div>
        </section>

        {/* ── SEO ──────────────────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6 space-y-4">
          <h2 className="text-base font-bold text-[#E0E0E0] border-b border-gray-800 pb-3">SEO</h2>
          <Input label="SEO Title (leave blank to auto-generate)" value={form.seo_title} onChange={v => set('seo_title', v)}
            placeholder={`${deviceLabel} ${form.board_revision} ${form.symptoms_summary[0] ?? ''} Repair — GameFix`.trim()} />
          <Textarea label="Meta Description" value={form.seo_description} onChange={v => set('seo_description', v)} rows={2}
            placeholder="Brief description for search results (120–160 chars)…" />
          <Input label="URL Slug (auto-generated if blank)" value={form.slug} onChange={v => set('slug', v)}
            placeholder="ps5-edm-030-no-power-f3502-short" />
          <TagInput label="Tags (auto + manual)" value={form.tags} onChange={v => set('tags', v)} />
        </section>

        {/* ── Media ────────────────────────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <MediaSection
            caseId={savedId.current ?? (isNew ? undefined : caseId)}
            media={form.media}
            onChange={v => set('media', v)}
          />
        </section>

        {/* ── Linked Board References ───────────────────────────────────────── */}
        <section className="bg-gray-900/40 border border-gray-800 rounded-2xl p-6">
          <LinkedReferencesAdminSection
            caseId={savedId.current ?? (isNew ? undefined : caseId)}
            onExpired={onExpired}
          />
        </section>

        {/* Bottom save bar */}
        <div className="flex items-center gap-3 pb-8 justify-end">
          <button onClick={() => handleSave(true)} disabled={saving} className={btn('secondary')}>
            Save & Add Another
          </button>
          <button onClick={() => handleSave(false)} disabled={saving} className={btn('primary')}>
            {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Route wrapper ─────────────────────────────────────────────────────────────
function AdminRepairKBContent({ onExpired }: { onExpired: () => void }) {
  const { id } = useParams<{ id?: string }>();
  const isEditor = !!id;
  return isEditor
    ? <CaseEditor caseId={id} onExpired={onExpired} />
    : <CaseList onExpired={onExpired} />;
}

export default function AdminRepairKBPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  const handleExpired = useCallback(() => {
    clearAdminAuth();
    setAuthed(false);
  }, []);
  return authed
    ? <AdminRepairKBContent onExpired={handleExpired} />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
